import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0AvatarList, F0Dialog, PersonAvatarVariant } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Delete, Plus, Sliders } from '@factorialco/f0-react/icons/app'
import {
  CommunityMemberEmployeeFragment,
  GroupMembersSearchDocument,
  RemoveMembersFromPostsGroupDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import 'modules/meetings/lib/utils/index.styles.css'
import { buildAvatarUrl } from 'modules/posts/lib/buildAvatarUrl'

type View = 'members' | 'invite' | 'automatic-invites'

type Props = {
  groupId: number
  groupTitle: string
  initialAvatars: PersonAvatarVariant[]
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
  showTrigger?: boolean
  canManageMembers?: boolean
  isDynamicCommunitiesEnabled?: boolean
  dynamicTeamId?: number | null
}

type GroupEmployee = CommunityMemberEmployeeFragment

export const MembersList: React.FC<Props> = ({
  groupId,
  groupTitle,
  initialAvatars,
  isOpen: controlledIsOpen,
  onOpenChange,
  showTrigger = true,
  canManageMembers = false,
  isDynamicCommunitiesEnabled = false,
  dynamicTeamId = null,
}) => {
  const { employee: currentEmployee } = useCurrentSession()
  const currentEmployeeId = currentEmployee?.id

  const initialTotalCount = initialAvatars.length
  const [internalIsOpen, setInternalIsOpen] = React.useState(false)
  const [view, setView] = React.useState<View>('members')
  const [isRemoving, setIsRemoving] = React.useState(false)

  const [removeMembers] = useMutation(RemoveMembersFromPostsGroupDocument, {
    extractorFn: (mutationData) => mutationData.postsMutations.removeMembersPostsGroup,
  })

  const isOpen = controlledIsOpen ?? internalIsOpen
  const setIsOpen = React.useCallback(
    (value: boolean) => {
      if (controlledIsOpen === undefined) {
        setInternalIsOpen(value)
      }
      onOpenChange?.(value)
    },
    [controlledIsOpen, onOpenChange]
  )

  const handleRemoveMembers = React.useCallback(
    async (memberIds: number[]) => {
      if (memberIds.length === 0) return

      setIsRemoving(true)

      const result = await removeMembers({
        variables: {
          id: groupId,
          employeeIds: memberIds,
        },
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('posts.communities.members_removed_error'),
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('posts.communities.members_removed_success'),
        })
      }

      setIsRemoving(false)
    },
    [groupId, removeMembers]
  )

  const secondaryActions = React.useMemo(() => {
    if (!canManageMembers) return undefined

    const actions = [
      {
        label: i18n.t('posts.communities.invite_people'),
        icon: Plus,
        onClick: () => setView('invite'),
      },
    ]

    if (isDynamicCommunitiesEnabled) {
      actions.unshift({
        label: i18n.t('posts.communities.set_automatic_invites'),
        icon: Sliders,
        onClick: () => setView('automatic-invites'),
      })
    }

    return actions
  }, [canManageMembers, isDynamicCommunitiesEnabled])

  const itemActions = React.useCallback(
    (item: GroupEmployee) => {
      if (!canManageMembers || item.id === currentEmployeeId) return []

      return [
        {
          label: i18n.t('posts.communities.remove_member'),
          icon: Delete,
          onClick: () => handleRemoveMembers([item.id]),
          disabled: isRemoving,
        },
      ]
    },
    [canManageMembers, currentEmployeeId, handleRemoveMembers, isRemoving]
  )

  const bulkActions = React.useCallback(() => {
    if (!canManageMembers) return { primary: [], secondary: [] }

    return {
      secondary: [],
      primary: [
        {
          id: 'remove',
          label: i18n.t('posts.communities.remove_members'),
        },
      ],
    }
  }, [canManageMembers])

  const handleBulkAction = React.useCallback(
    (action: string, items: GroupEmployee[]) => {
      if (action === 'remove') {
        const memberIds = items.map((item) => item.id)
        handleRemoveMembers(memberIds)
      }
    },
    [handleRemoveMembers]
  )

  const dataSource = useGraphqlDataCollectionSource(
    {
      document: GroupMembersSearchDocument,
      mapData: (data) =>
        data.posts?.group?.employeesConnection?.edges
          ?.map((edge) => edge?.node)
          .filter((node): node is GroupEmployee => node !== null) ?? [],
      mapVariables: ({ search }) => ({
        id: groupId,
        fuzzySearch: search || undefined,
      }),
      mapPagination: (data) => ({
        total: data.posts?.group?.employeesConnection?.totalCount ?? 0,
        cursor: data.posts?.group?.employeesConnection?.pageInfo?.endCursor ?? undefined,
        hasNextPage: data.posts?.group?.employeesConnection?.pageInfo?.hasNextPage ?? false,
      }),
      paginationType: 'infinite-scroll',
      search: {
        enabled: true,
      },
      secondaryActions: secondaryActions
        ? {
            expanded: 2 satisfies 0 | 1 | 2,
            actions: () => secondaryActions,
          }
        : undefined,
      itemActions: canManageMembers ? itemActions : undefined,
      selectable: canManageMembers
        ? (item: GroupEmployee) => (item.id === currentEmployeeId ? undefined : item.id)
        : undefined,
      bulkActions: canManageMembers ? bulkActions : undefined,
    },
    [groupId, secondaryActions, canManageMembers, itemActions, bulkActions, currentEmployeeId]
  )

  // Modal is open when controlled or internal state says so
  const modalOpen = isOpen

  const handleCloseModal = React.useCallback(() => {
    setView('members') // Reset to default view
    setIsOpen(false)
  }, [setIsOpen])

  const MAX_VISIBLE_AVATARS = 3
  const visibleAvatars = initialAvatars.slice(0, MAX_VISIBLE_AVATARS)
  const remainingCount =
    initialTotalCount > MAX_VISIBLE_AVATARS ? initialTotalCount - MAX_VISIBLE_AVATARS : 0

  return (
    <>
      {showTrigger && (
        <Box cursor="pointer" onClick={() => setIsOpen(true)} width="fit-content">
          <F0AvatarList
            avatars={visibleAvatars}
            remainingCount={remainingCount}
            size="sm"
            type="person"
          />
        </Box>
      )}
      <F0Dialog
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={i18n.t('posts.communities.members_title', {
          membersCount: initialTotalCount,
          groupTitle,
        })}
        width="lg"
        secondaryAction={
          view !== 'members'
            ? {
                label: i18n.t('common.cancel'),
                onClick: () => setView('members'),
              }
            : undefined
        }
      >
        {modalOpen && (
          <>
            {view === 'members' && (
              <Box style={{ height: '400px' }} className={'utils_hiddenScrollBar'}>
                <OneDataCollection
                  source={dataSource}
                  fullHeight
                  emptyStates={{
                    'no-data': {
                      title: i18n.t('posts.communities.no_result'),
                    },
                  }}
                  onBulkAction={(action, items) =>
                    handleBulkAction(
                      action,
                      items.itemsStatus.filter((item) => item.checked).map((item) => item.item)
                    )
                  }
                  visualizations={[
                    {
                      type: 'table',
                      options: {
                        columns: [
                          {
                            label: i18n.t('posts.communities.members_name'),
                            render: (item: GroupEmployee) => {
                              const avatarUrl = buildAvatarUrl(item.avatar)
                              return {
                                type: 'person',
                                value: {
                                  firstName: item.firstName ?? '',
                                  lastName: item.lastName ?? '',
                                  src: avatarUrl,
                                },
                              }
                            },
                          },
                          {
                            label: i18n.t('posts.communities.members_role'),
                            render: (item: GroupEmployee) => {
                              const role = item?.referenceContract?.jobCatalogLevel?.roleName ?? ''
                              return {
                                type: 'text',
                                value: role,
                              }
                            },
                          },
                        ],
                      },
                    },
                  ]}
                />
              </Box>
            )}
            {view === 'invite' && (
              <Box padding="s16">
                {/* Invite Members view - to be implemented in another component in Task 3 */}
                Invite Members view placeholder
              </Box>
            )}
            {view === 'automatic-invites' && (
              <Box padding="s16">
                {/* Automatic Invites view - to be implemented in another component in Task 4 */}
                Automatic Invites view placeholder
              </Box>
            )}
          </>
        )}
      </F0Dialog>
    </>
  )
}
